import type { TaxableReceiptTypes } from '@/entities/finance/FinancesSettings'
import type { AccountingPlanType } from '@/entities/finance/accounting/AccountingPlan'
import type { PaymentMethodType } from '@/entities/finance/payment/Payment'
import type { OrganizationCountryCode } from '@/entities/organization/Organization'
import type { CountryCode } from '@/utils/address'
import { z } from '@/utils/zod'

type CountrySpecificSchemaKey = Lowercase<OrganizationCountryCode>
type CountrySpecificSchemaShape = Record<CountrySpecificSchemaKey, z.ZodType>
type InvalidCountrySpecificSchemaKeys<Shape> = Exclude<keyof Shape, CountrySpecificSchemaKey>

export const createCountrySpecificSchema = <const Shape extends z.ZodRawShape & CountrySpecificSchemaShape>(
    shape: Shape & Record<InvalidCountrySpecificSchemaKeys<Shape>, never>
) => z.strictObject(shape)

export type OrganizationCountryProfile = {
    timezone: boolean
    holidayBlocking: boolean
    finances: {
        hasVatId: boolean
        paymentMethods: readonly PaymentMethodType[]
    }
    // Unit of distances entered by the organization, e.g. the mileage of compensation claims
    distanceUnit: 'km' | 'mi'
    accounting: {
        hasStockReport: boolean
        accountingPlans: Array<Exclude<AccountingPlanType, 'TEST'>>
        hasDatev: boolean
        hasGobd: boolean
        donationType: 'EU' | 'US'
        taxableReceiptTypes: TaxableReceiptTypes
    }
    donations: {
        confirmationRequired: boolean
    }
    payments: {
        // Whether payouts to contacts are made by SEPA credit transfer, which requires an IBAN
        sepaCreditTransfer: boolean
    }
    compensation: {
        // Yearly tax-free allowances offered when compensation claims are set up; amounts stay editable
        limitPresets: { key: 'trainerAllowance' | 'volunteerAllowance'; amount: number }[]
        // Official mileage rate per kilometre offered for the travel cost rate, null where there is none
        mileageAmount: number | null
    }
}

const defaultOrganizationCountryProfile: OrganizationCountryProfile = {
    timezone: false,
    holidayBlocking: true,
    finances: {
        hasVatId: true,
        paymentMethods: ['sepaCreditTransfer', 'sepaDirectDebit', 'cash', 'online'],
    },
    distanceUnit: 'km',
    accounting: {
        hasStockReport: true,
        accountingPlans: ['SKR42EUR', 'SKR42GUV', 'SKR03GUV', 'SKR04GUV'],
        hasDatev: true,
        hasGobd: false,
        donationType: 'EU',
        taxableReceiptTypes: 'all',
    },
    donations: {
        confirmationRequired: true,
    },
    payments: {
        sepaCreditTransfer: true,
    },
    compensation: {
        limitPresets: [],
        mileageAmount: null,
    },
}

export const organizationCountryProfiles = {
    DE: {
        accounting: {
            ...defaultOrganizationCountryProfile.accounting,
            hasGobd: true,
        },
        compensation: {
            // Übungsleiterpauschale and Ehrenamtspauschale (§ 3 Nr. 26 and 26a EStG) from 2026
            limitPresets: [
                { key: 'trainerAllowance', amount: 3300_00 },
                { key: 'volunteerAllowance', amount: 960_00 },
            ],
            // Tax-free travel cost reimbursement for trips by car
            mileageAmount: 30,
        },
    },
    AT: {
        accounting: {
            ...defaultOrganizationCountryProfile.accounting,
            hasStockReport: false,
            accountingPlans: ['SKR07EAR', 'SKR07GUV'],
        },
        compensation: {
            limitPresets: [],
            // Amtliches Kilometergeld for cars from 2025
            mileageAmount: 50,
        },
    },
    US: {
        timezone: true,
        holidayBlocking: false,
        finances: {
            hasVatId: false,
            paymentMethods: ['sepaCreditTransfer', 'cash', 'online'],
        },
        accounting: {
            hasStockReport: true,
            accountingPlans: ['USGUV'],
            hasDatev: false,
            hasGobd: false,
            donationType: 'US',
            taxableReceiptTypes: 'outgoing',
        },
        distanceUnit: 'mi',
        payments: {
            sepaCreditTransfer: false,
        },
    },
} satisfies Partial<Record<CountryCode, Partial<OrganizationCountryProfile>>>

export const getOrganizationCountryProfile = (country: CountryCode): OrganizationCountryProfile => {
    return {
        ...defaultOrganizationCountryProfile,
        ...organizationCountryProfiles[country as keyof typeof organizationCountryProfiles],
    }
}
